import type { ImperativePanelHandle } from 'react-resizable-panels';

/**
 * The panel's current size, or `null` when it cannot be read.
 *
 * `getSize()` throws until the group has laid out.
 *
 * @param panel - The panel's imperative handle, if it has mounted
 * @returns The size as a percentage, or null while the group has no layout
 */
export function readPanelSize(
  panel: ImperativePanelHandle | null | undefined,
): number | null {
  try {
    return panel?.getSize() ?? null;
  } catch {
    return null;
  }
}

/**
 * Resizes the panel, ignoring the pre-layout window where it cannot be sized.
 *
 * @param panel - The panel's imperative handle, if it has mounted
 * @param size - Target size, as a percentage of the group
 * @returns true when the panel was resized
 */
export function applyPanelSize(
  panel: ImperativePanelHandle | null | undefined,
  size: number,
): boolean {
  if (!panel) return false;
  try {
    panel.resize(size);
    return true;
  } catch {
    return false;
  }
}

/**
 * Collapses or expands a collapsible panel, ignoring the pre-layout window.
 *
 * Expanding returns the panel to its size before it collapsed, or to
 * `minSize` when it has none yet.
 *
 * @param panel - The panel's imperative handle, if it has mounted
 * @param isExpanded - Whether the panel should be open
 * @param minSize - Size to open at when it has never been open, as a percentage
 * @returns true when the panel was changed
 */
export function setPanelExpanded(
  panel: ImperativePanelHandle | null | undefined,
  isExpanded: boolean,
  minSize?: number,
): boolean {
  if (!panel) return false;
  try {
    if (isExpanded) panel.expand(minSize);
    else panel.collapse();
    return true;
  } catch {
    return false;
  }
}
